/**
 * InvoiceFormAddresses
 *
 * Address cards section: billing/client address and shipping/consignee address.
 * Also renders the shipping documentation fields for export goods.
 */

import type { Address } from '@/gen/proto'

import { getBillingAddressLabel } from './invoice-form-helpers'
import { AddressCardWithActions, EmptyAddressButton, ShippingDocumentation, getShippingEmptyLabel } from './InvoiceFormAddresses-parts'
import { addressHasUsage } from '../../../utils/billing-helpers'

import type { UseInvoiceFormReturn } from './useInvoiceForm'

interface InvoiceFormAddressesProps {
  form: UseInvoiceFormReturn
  isVendor: boolean
  isLoading: boolean
  partyLabel: string
  onOpenBillingSelector: () => void
  onOpenShippingSelector: () => void
}

function deriveBillingDisplay(form: UseInvoiceFormReturn): Address | null | undefined {
  const { invoiceType, exportType, selectedShipToAddress, selectedConsigneeAddress, allPartyAddresses } = form
  if (invoiceType === 'export' && exportType === 'services') {
    return selectedConsigneeAddress ?? allPartyAddresses.find((a) => addressHasUsage(a, 'billing'))
  }
  return selectedShipToAddress
}

function deriveConsigneeLabel(invoiceType: string, isVendor: boolean): string {
  if (invoiceType === 'export') { return 'Consignee Address' }
  if (isVendor) { return 'Delivery Address' }
  return 'Shipping Address'
}

interface BillingSlotProps {
  form: UseInvoiceFormReturn
  isServicesExport: boolean
  billingLabel: string
  isB2bOrExportRequired: boolean
  displayAddress: Address | null | undefined
  onOpenBillingSelector: () => void
  onOpenShippingSelector: () => void
}

function BillingAddressSlot(p: BillingSlotProps): JSX.Element {
  const { form, isServicesExport, billingLabel, isB2bOrExportRequired, displayAddress } = p
  const { setSelectedShipToAddress, setShipToType, setSelectedConsigneeAddress, allPartyAddresses } = form
  const onChange = isServicesExport ? p.onOpenShippingSelector : p.onOpenBillingSelector
  const onRemove = (): void => {
    if (isServicesExport) {
      setSelectedConsigneeAddress(null)
    } else {
      setSelectedShipToAddress(null)
      setShipToType('custom')
    }
  }
  const hasBillingAddr = allPartyAddresses.filter((a) => addressHasUsage(a, 'billing')).length > 0
  const emptyLabel = hasBillingAddr ? 'Click to Select Address' : 'Please add a billing address to the party first'

  return (
    <div>
      <h4 className="text-sm font-semibold text-foreground mb-2 pl-4">
        {billingLabel}
        {isB2bOrExportRequired && <span className="text-red-500 ml-0.5">*</span>}
      </h4>
      {displayAddress ? (
        <AddressCardWithActions address={displayAddress} onChangeClick={onChange} onRemove={onRemove} />
      ) : (
        <EmptyAddressButton label={emptyLabel} onClick={onChange} />
      )}
    </div>
  )
}

interface ConsigneeSlotProps {
  form: UseInvoiceFormReturn
  isVendor: boolean
  consigneeAddressLabel: string
  onOpenShippingSelector: () => void
}

function ConsigneeAddressSlot(p: ConsigneeSlotProps): JSX.Element {
  const { form, isVendor, consigneeAddressLabel, onOpenShippingSelector } = p
  const { selectedConsigneeAddress, setSelectedConsigneeAddress, setConsigneeType, allPartyAddresses, orgAddresses, invoiceType } = form
  const onRemove = (): void => {
    setSelectedConsigneeAddress(null)
    setConsigneeType('custom')
  }
  return (
    <div>
      <h4 className="text-sm font-semibold text-foreground mb-2 pl-4">
        {consigneeAddressLabel}
        <span className="text-foreground-secondary ml-1 text-xs">(Optional)</span>
      </h4>
      {selectedConsigneeAddress ? (
        <AddressCardWithActions address={selectedConsigneeAddress} onChangeClick={onOpenShippingSelector} onRemove={onRemove} />
      ) : (
        <EmptyAddressButton
          icon="shipping"
          label={getShippingEmptyLabel(isVendor, orgAddresses, allPartyAddresses, invoiceType)}
          onClick={onOpenShippingSelector}
        />
      )}
    </div>
  )
}

interface AddressGridProps {
  form: UseInvoiceFormReturn
  isVendor: boolean
  partyLabel: string
  onOpenBillingSelector: () => void
  onOpenShippingSelector: () => void
}

function AddressesCardBody(p: AddressGridProps): JSX.Element {
  const { form, isVendor, partyLabel } = p
  const { invoiceType, exportType, selectedParty } = form
  const billingLabel = getBillingAddressLabel(invoiceType, isVendor)
  const isB2bOrExportRequired = invoiceType === 'b2b' || invoiceType === 'export'
  const displayAddress = deriveBillingDisplay(form)
  const isServicesExport = invoiceType === 'export' && exportType === 'services'
  const consigneeAddressLabel = deriveConsigneeLabel(invoiceType, isVendor)

  if (!selectedParty) {
    return (
      <p className="text-muted-foreground text-sm">
        Please select a {partyLabel.toLowerCase()} first
      </p>
    )
  }
  return (
    <div className={`grid grid-cols-1 ${isServicesExport ? '' : 'lg:grid-cols-2'} gap-4`}>
      <BillingAddressSlot
        billingLabel={billingLabel}
        displayAddress={displayAddress}
        form={form}
        isB2bOrExportRequired={isB2bOrExportRequired}
        isServicesExport={isServicesExport}
        onOpenBillingSelector={p.onOpenBillingSelector}
        onOpenShippingSelector={p.onOpenShippingSelector}
      />
      {!isServicesExport && (
        <ConsigneeAddressSlot
          consigneeAddressLabel={consigneeAddressLabel}
          form={form}
          isVendor={isVendor}
          onOpenShippingSelector={p.onOpenShippingSelector}
        />
      )}
    </div>
  )
}

export function InvoiceFormAddresses(props: InvoiceFormAddressesProps): JSX.Element {
  const { form, isLoading } = props
  const { invoiceType, exportType } = form
  return (
    <>
      <div className="card el-p-md">
        <h3 className="text-xs font-bold tracking-wider uppercase -mx-3 -mt-[10px] mb-[10px] px-3 py-2 bg-primary-solid text-primary-foreground rounded-t">
          Addresses
        </h3>
        <AddressesCardBody
          form={form}
          isVendor={props.isVendor}
          partyLabel={props.partyLabel}
          onOpenBillingSelector={props.onOpenBillingSelector}
          onOpenShippingSelector={props.onOpenShippingSelector}
        />
      </div>
      {invoiceType === 'export' && exportType === 'goods' && (
        <ShippingDocumentation form={form} isLoading={isLoading} />
      )}
    </>
  )
}
